import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { DialogModule } from 'primeng/dialog';

import { CommandPaletteService, SearchEntry } from './command-palette.service';

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [DialogModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [visible]="palette.isOpen()"
      (visibleChange)="onVisibleChange($event)"
      [modal]="true"
      [dismissableMask]="true"
      [showHeader]="false"
      [draggable]="false"
      [resizable]="false"
      position="top"
      styleClass="command-palette-dialog"
      contentStyleClass="!p-0"
      [style]="{ width: '40rem', maxWidth: '92vw', marginTop: '12vh' }"
      (onShow)="onShow()"
    >
      <div class="flex flex-col max-h-[70vh]">
        <!-- Input -->
        <div class="flex items-center gap-3 px-4 py-3 border-b border-surface-200 dark:border-surface-700">
          <i class="pi pi-search text-surface-400 text-lg"></i>
          <input
            #searchInput
            type="text"
            class="flex-1 bg-transparent border-0 outline-none text-lg text-surface-900 dark:text-surface-0 placeholder:text-surface-400"
            placeholder="Buscar páginas…"
            [ngModel]="palette.query()"
            (ngModelChange)="onQueryChange($event)"
            (keydown.arrowDown)="move($event, 1)"
            (keydown.arrowUp)="move($event, -1)"
            (keydown.enter)="submit()"
          />
          <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-lg text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
            title="Cerrar (Esc)"
            aria-label="Cerrar"
            (click)="palette.close()"
          >
            <i class="pi pi-times text-sm"></i>
          </button>
        </div>

        <!-- Resultados -->
        <div class="overflow-y-auto py-2">
          @if (displayed().length === 0) {
            @if (palette.query().trim()) {
              <div class="flex flex-col items-center gap-3 py-10 text-center">
                <i class="pi pi-inbox text-4xl text-surface-400"></i>
                <p class="text-surface-500">Sin resultados para "{{ palette.query() }}"</p>
              </div>
            } @else {
              <div class="flex flex-col items-center gap-3 py-10 text-center">
                <i class="pi pi-compass text-4xl text-surface-400"></i>
                <p class="text-surface-500">Escribe para buscar entre todas tus páginas</p>
              </div>
            }
          } @else {
            @if (!palette.query().trim()) {
              <div class="flex items-center justify-between px-4 pt-1 pb-2">
                <p class="text-xs font-semibold tracking-wide text-surface-500 uppercase">Recientes</p>
                <button
                  type="button"
                  class="text-xs font-medium text-surface-400 hover:text-primary transition-colors"
                  (click)="palette.clearRecents()"
                >
                  Limpiar
                </button>
              </div>
            }
            @for (entry of displayed(); track entry.id; let i = $index) {
              <button
                type="button"
                [id]="'cmdp-row-' + i"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                [class.bg-primary-50]="i === activeIndex()"
                [class.dark:bg-primary-900]="i === activeIndex()"
                (mouseenter)="activeIndex.set(i)"
                (click)="go(entry)"
              >
                <i [class]="entry.icon" class="text-surface-500 w-5 text-center"></i>
                <span class="flex-1 min-w-0">
                  <span class="block truncate text-surface-900 dark:text-surface-0">{{ entry.label }}</span>
                  <span class="block truncate text-xs text-surface-500">
                    {{ entry.group }}{{ entry.description ? ' · ' + entry.description : '' }}
                  </span>
                </span>
              </button>
            }
          }
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-between px-4 py-2 border-t border-surface-200 dark:border-surface-700 text-xs text-surface-500"
        >
          <span class="flex items-center gap-3">
            <span>↑↓ navegar</span>
            <span>↵ abrir</span>
            <span>esc cerrar</span>
          </span>
          <span>{{ displayed().length }} resultado{{ displayed().length === 1 ? '' : 's' }}</span>
        </div>
      </div>
    </p-dialog>
  `,
})
export class CommandPaletteComponent {
  protected readonly palette = inject(CommandPaletteService);
  private readonly router = inject(Router);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly activeIndex = signal(0);

  protected readonly displayed = computed<SearchEntry[]>(() =>
    this.palette.query().trim() ? this.palette.results() : this.palette.recentEntries()
  );

  constructor() {
    // Reiniciar la selección cuando cambian los resultados visibles.
    effect(() => {
      this.displayed();
      this.activeIndex.set(0);
    });
  }

  onShow(): void {
    this.searchInput()?.nativeElement.focus();
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) this.palette.close();
  }

  onQueryChange(value: string): void {
    this.palette.query.set(value);
  }

  move(event: Event, delta: number): void {
    event.preventDefault();
    const list = this.displayed();
    if (list.length === 0) return;
    const next = Math.max(0, Math.min(list.length - 1, this.activeIndex() + delta));
    this.activeIndex.set(next);
    document.getElementById(`cmdp-row-${next}`)?.scrollIntoView({ block: 'nearest' });
  }

  submit(): void {
    const entry = this.displayed()[this.activeIndex()];
    if (entry) this.go(entry);
  }

  go(entry: SearchEntry): void {
    this.palette.addRecent(entry.id);
    this.palette.close();
    this.router.navigate(entry.routerLink);
  }
}
